import {
  LayoutServiceData,
  Placeholder,
  PlaceholdersData,
} from '@sitecore-jss/sitecore-jss-nextjs';
import Head from 'next/head';
import { createElement } from 'react';

import { useSite } from 'src/hooks/useSite';
import { useTemplate } from 'src/hooks/useTemplate';

import { getPublicUrl } from 'src/utils/getPublicUrl';
import { getComponent } from 'src/utils/sitecoreHelpers';

import Logo from 'src/assets/icons/logo.svg';
import TCLogo from 'src/assets/icons/tc-home-logo-black.svg';

import RichText from 'components/BaseHelpers/RichText';
import { NavigationFooterProps } from 'components/Navigation/NavigationFooter';

import styles from './PdfTemplate.module.scss';

type PdfConfigOptions = {
  y: number;
  width: number;
  height: number;
  children?: JSX.Element;
};

// 6.25in * 72 pixels per inch = 450
const BROWSER_WIDTH = 450;

const PdfOptions = () =>
  createElement('pdf:options', {
    topdown: 'true',
    units: 'inches',
    addlinks: 'true',
  });

const PdfBody = ({ y, width, height, children }: PdfConfigOptions) =>
  createElement(
    'pdf:body',
    {
      x: ((8.5 - width) / 2).toFixed(4),
      y,
      width,
      height,
      browserwidth: BROWSER_WIDTH,
      startatpage: '1',
    },
    children
  );

const PdfFooter = ({ y, width, height, children }: PdfConfigOptions) =>
  createElement(
    'pdf:footer',
    {
      x: ((8.5 - width) / 2).toFixed(4),
      y,
      width,
      height,
      browserwidth: BROWSER_WIDTH,
      startatpage: '1',
    },
    children
  );

const PdfPageNumber = () => createElement('pdf:PageNumber');

export type PdfTemplateProps = {
  layoutData: LayoutServiceData;
};

const PdfTemplate = ({ layoutData }: PdfTemplateProps): JSX.Element => {
  const { route } = layoutData.sitecore;
  const { isTCSite } = useSite();
  const { isNewsPublicationEventDetail } = useTemplate();

  // Only these components will render in the PDF. Omitting components
  // that cross link to other pages.
  const pdfComponents = [
    'BasicContentAccordion',
    'BasicContentAttachments',
    'BasicContentFirmFeature',
    'BasicContentHeadlineAndAbstract',
    'BasicContentImage',
    'BasicContentMediaGrid',
    'BasicContentPullQuote',
    'BasicContentRichText',
    'InsightsAgenda',
    'InsightsInsightHeader',
    'InsightsPeopleRelatedContent',
    'PeopleAdmissions',
    'PeopleAuthors',
    'PeopleContacts',
    'PeopleEducation',
    'PeopleLanguages',
    'PeopleProfessionalHeader',
    'PeopleSpeakers',
  ];

  const filterComponents = (component: PlaceholdersData[string][number]) =>
    'componentName' in component && pdfComponents.includes(component.componentName);

  if (!route?.placeholders) {
    return <></>;
  }

  route.placeholders['page-header'] = route.placeholders['page-header'].filter(filterComponents);
  route.placeholders['content'] = route.placeholders['content'].filter(filterComponents);
  route.placeholders['page-footer'] = route.placeholders['page-footer'].filter(filterComponents);

  const width = 6.25;
  const spacingY = 0.5;
  const footerHeight = 0.75;
  const bodyHeight = 11 - spacingY * 2 - footerHeight;
  const footerY = 11 - footerHeight;

  const getDisclaimerText = () => {
    const footer = getComponent<NavigationFooterProps>(
      'NavigationFooter',
      route.placeholders['footer']
    );

    const disclaimer = footer?.fields?.disclaimerText;
    return disclaimer;
  };

  return (
    <>
      <Head>
        <base href={getPublicUrl(isTCSite)} />
      </Head>
      <div className={styles.main} id="pdf">
        <PdfOptions />
        <PdfBody width={width} y={spacingY} height={bodyHeight}>
          <>
            {isTCSite ? <TCLogo className={styles.logo} /> : <Logo className={styles.logo} />}
            <Placeholder name="page-header" rendering={route} />
            <Placeholder name="content" rendering={route} />
            <Placeholder name="page-footer" rendering={route} />
            {isNewsPublicationEventDetail && (
              <RichText
                className={styles.disclaimer}
                field={getDisclaimerText()}
                style={{
                  pageBreakInside: 'avoid',
                }}
              />
            )}
          </>
        </PdfBody>
        <PdfFooter width={width} y={footerY} height={footerHeight}>
          {/*
              For some reason, applying a class to the footer elements would not work
              so we are setting the font via inline styles
            */}
          <div
            style={{
              fontFamily: 'Mulish',
              fontSize: '9px',
              textAlign: 'center',
            }}
          >
            <PdfPageNumber />
          </div>
        </PdfFooter>
      </div>
    </>
  );
};

export default PdfTemplate;
